/**
 * EPSG:27700 (British National Grid) <-> local viewer-space conversions.
 *
 * Hard constraint (GOAL.md #4): EPSG:27700 throughout; 1 unit = 1 metre;
 * local origin E 390978 N 586358; AOI centre E 394500 N 588200. The origin
 * and the AOI centre are different points — do not conflate them anywhere
 * in this module or its callers.
 *
 * Viewer axis convention (parent spec §6, PRD R4):
 *   x = easting  - LOCAL_ORIGIN_E              (east = +x)
 *   y = elevation, metres AOD (1:1, no vertical exaggeration by default)
 *   z = -(northing - LOCAL_ORIGIN_N)           (north = -z)
 *
 * North maps to -z (not +z) deliberately: three.js is y-up and a default
 * camera looks down -z, so mapping north to -z makes the first-person view
 * un-mirrored — facing north puts east on the right, matching the real map
 * and both minimaps. (With north = +z the horizontal view came out
 * east-west mirrored.) Terrain tiles are baked to match (scripts/03), and
 * everything else derives from bngToLocal, so the whole scene is consistent.
 */

export const LOCAL_ORIGIN_EASTING = 390978;
export const LOCAL_ORIGIN_NORTHING = 586358;

/** AOI centre — distinct from the local origin. Never used as the origin. */
export const AOI_CENTRE_EASTING = 394500;
export const AOI_CENTRE_NORTHING = 588200;

export interface LocalXZ {
  x: number;
  z: number;
}

export interface BngEN {
  easting: number;
  northing: number;
}

/** BNG easting/northing -> local viewer-space x/z (metres, 1 unit = 1 m). */
export function bngToLocal(easting: number, northing: number): LocalXZ {
  return {
    x: easting - LOCAL_ORIGIN_EASTING,
    z: LOCAL_ORIGIN_NORTHING - northing, // north = -z (written this way to avoid -0)
  };
}

/** Local viewer-space x/z -> BNG easting/northing. Exact inverse of bngToLocal. */
export function localToBng(x: number, z: number): BngEN {
  return {
    easting: x + LOCAL_ORIGIN_EASTING,
    northing: LOCAL_ORIGIN_NORTHING - z, // inverse of z = -(north - origin)
  };
}

/**
 * Compass bearing (degrees, 0 = North, clockwise positive, range [0, 360))
 * from a horizontal forward direction expressed in viewer-space (dx, dz).
 * North is -z (decreasing z = increasing northing); East is +x — consistent
 * with the axis convention above. So the north component of (dx, dz) is -dz.
 */
export function bearingDegFromForward(dx: number, dz: number): number {
  const deg = (Math.atan2(dx, -dz) * 180) / Math.PI;
  return (deg + 360) % 360;
}

/**
 * Horizontal forward direction (dx, dz), unit length, for a given compass
 * bearing in degrees. Exact inverse of bearingDegFromForward for the
 * horizontal plane (no pitch component).
 */
export function forwardFromBearingDeg(bearingDeg: number): LocalXZ {
  const rad = (bearingDeg * Math.PI) / 180;
  return { x: Math.sin(rad), z: -Math.cos(rad) }; // north (bearing 0) = -z
}

/** Planar distance in metres between two local-space points (y ignored). */
export function planarDistance(a: LocalXZ, b: LocalXZ): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

/**
 * three.js camera yaw <-> compass bearing.
 *
 * A three.js Object3D.rotateY(yaw) applied to the camera's default
 * forward vector (0, 0, -1) yields (-sin(yaw), -cos(yaw)) in (x, z). These
 * two helpers convert between that yaw and the compass bearing produced
 * by bearingDegFromForward, so camera/cameraRig.ts's quaternion-based
 * result can be cross-checked against this DOM-free implementation.
 */
export function forwardFromYaw(yawRad: number): LocalXZ {
  return { x: -Math.sin(yawRad), z: -Math.cos(yawRad) };
}

export function yawFromBearingDeg(bearingDeg: number): number {
  const { x, z } = forwardFromBearingDeg(bearingDeg);
  return Math.atan2(-x, -z);
}
